import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ArrowRight, MessageCircle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import SEO from "@/components/SEO";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useBooking } from "@/components/booking";
import { SERVICES, waServiceLink, BUSINESS } from "@/data/business";

export default function Services() {
  const { openBooking } = useBooking();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const t = setTimeout(() => {
        document.querySelector(location.hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 350);
      return () => clearTimeout(t);
    }
  }, [location.hash]);

  return (
    <main className="bg-ivory">
      <SEO
        title="Services"
        description="Hair, hair colour, keratin treatments, braiding, waxing & shaving, makeup and bridal services at Glam Girl Makeup Studio, Patel Nagar / Hardattpur, Varanasi. Enquire for pricing."
      />
      <section className="grain mx-auto max-w-7xl px-5 pb-16 pt-32 sm:px-8 lg:pt-40" data-testid="services-header">
        <Reveal>
          <Eyebrow>Service Catalogue</Eyebrow>
          <h1 className="mt-4 max-w-2xl font-serif text-4xl font-medium leading-[1.08] tracking-tight text-charcoal sm:text-5xl lg:text-6xl">
            Everything for your hair, <em className="text-rose">your way.</em>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-charcoal/65">
            Seven categories, one studio. Choose a service and book — or message us on
            WhatsApp and we'll help you decide.
          </p>
          <p className="mt-4 inline-flex items-center gap-2 rounded-full border border-rose/30 bg-rose-soft/30 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-rose-deep" data-testid="pricing-note">
            Enquire for pricing — we quote honestly, per service
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8 lg:pb-32">
        <div className="grid gap-10 lg:grid-cols-[240px_1fr]">
          <aside className="hidden lg:block">
            <nav aria-label="Service categories" className="sticky top-28 space-y-1">
              {SERVICES.map((c, i) => (
                <a
                  key={c.id}
                  href={`#${c.id}`}
                  data-testid={`services-nav-${c.id}`}
                  className="group flex items-baseline gap-3 rounded-lg px-3 py-2.5 text-sm text-charcoal/60 transition-colors hover:bg-cream hover:text-charcoal"
                >
                  <span className="font-serif italic text-rose">{String(i + 1).padStart(2, "0")}</span>
                  {c.name}
                </a>
              ))}
            </nav>
          </aside>

          <Accordion type="multiple" defaultValue={SERVICES.map((c) => c.id)} className="space-y-5">
            {SERVICES.map((cat, i) => (
              <Reveal key={cat.id} delay={0.04 * i}>
                <AccordionItem
                  value={cat.id}
                  id={cat.id}
                  data-testid={`services-category-${cat.id}`}
                  className="scroll-mt-28 overflow-hidden rounded-2xl border border-charcoal/10 bg-white px-7 transition-colors data-[state=open]:border-rose/25"
                >
                  <AccordionTrigger
                    data-testid={`services-category-${cat.id}-trigger`}
                    className="group py-6 hover:no-underline [&[data-state=open]>div>span.plus]:rotate-45"
                  >
                    <div className="flex flex-1 items-baseline gap-5 text-left">
                      <span className="font-serif text-lg italic text-rose">{String(i + 1).padStart(2, "0")}</span>
                      <div>
                        <h2 className="font-serif text-2xl font-medium text-charcoal sm:text-3xl">{cat.name}</h2>
                        <p className="mt-1 text-sm text-charcoal/55">{cat.tagline}</p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <ul className="divide-y divide-charcoal/8 border-t border-charcoal/10">
                      {cat.items.map((service) => (
                        <li
                          key={service}
                          className="group/item flex flex-col gap-3 py-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between"
                          data-testid={`service-item-${service.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        >
                          <span className="text-[15px] font-medium text-charcoal">{service}</span>
                          <span className="flex items-center gap-2">
                            <a
                              href={waServiceLink(service)}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`service-whatsapp-${service.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                              aria-label={`Enquire about ${service} on WhatsApp`}
                              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-charcoal/15 text-charcoal/60 transition-colors hover:border-rose hover:text-rose"
                            >
                              <MessageCircle className="h-4 w-4" />
                            </a>
                            <button
                              onClick={() => openBooking(service)}
                              data-testid={`service-book-${service.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full bg-charcoal px-5 py-3 text-xs font-semibold tracking-wide text-ivory transition-colors hover:bg-rose-deep sm:flex-none sm:py-2.5"
                            >
                              Book This Service
                              <ArrowRight className="h-3.5 w-3.5" />
                            </button>
                          </span>
                        </li>
                      ))}
                    </ul>
                    <p className="pb-5 pt-1 text-xs text-charcoal/45">
                      Enquire for pricing · {BUSINESS.hoursLine}
                    </p>
                  </AccordionContent>
                </AccordionItem>
              </Reveal>
            ))}
          </Accordion>
        </div>
      </section>
    </main>
  );
}
